import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const DATA_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";


const STOCK_DETAIL_PATH = "/stocks2"; // Change to "/stock" or another base path if your router uses a different route.

const initialFilters = {
  minMarketCap: "",
  maxMarketCap: "",
  minPeg: "",
  maxPeg: "",
  minForwardPE: "",
  maxForwardPE: "",
  minPB: "",
  maxPB: "",
  minPTBV: "",
  maxPTBV: "",
  minBuybackYield: "",
  maxBuybackYield: "",
  minGrowthRate: "",
  maxGrowthRate: "",
  minInstitutionalOwnership: "",
  maxInstitutionalOwnership: "",
  minRsi14: "",
  maxRsi14: "",
  minBeta: "",
  maxBeta: "",
  minPctAbove52WeekLow: "",
  maxPctAbove52WeekLow: "",
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

export default function StockScreener() {
  const isMobile = useIsMobile(768);

  const [rows, setRows] = useState([]);
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("marketCap");
  const [sortDir, setSortDir] = useState("desc");
  const [filters, setFilters] = useState(initialFilters);
  const [spyStats, setSpyStats] = useState({});

  const visibleColumns = useMemo(() => {
    if (!isMobile) return columns;

    return columns
      .filter((col) => ["ticker", "price", "marketCap", "rsi14", "beta","Revenue","EBITDA","pb","peg","forwardPE","growthRate","buybackYield","dividendYield","institutionalOwnership"].includes(col.key))
      .map((col) => {
        if (col.key === "ticker") {
          return {
            ...col,
            sticky: true,
            left: 0,
            width: 82,
          };
        }

        return {
          ...col,
          sticky: false,
          left: undefined,
          width:
            col.key === "marketCap" ? 100 :
            col.key === "price" ? 72 :
            col.key === "rsi14" ? 72 :
            col.key === "beta" ? 72 :
            col.key === "Revenue" ? 72 :
            col.key === "EBITDA" ? 72 :
            col.key === "pb" ? 72 :
            col.key === "peg" ? 72 :
            col.key === "forwardPE" ? 72 :
            col.key === "growthRate" ? 72 :
            col.key === "buybackYield" ? 72 :
            col.key === "dividendYield" ? 72 :
            col.key === "institutionalOwnership" ? 72 :
            col.width,
        };
      });
  }, [isMobile]);


  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(DATA_URL);
        const text = await res.text();

        if (!res.ok) {
          throw new Error(`Fetch failed: ${res.status}`);
        }

        if (text.trim().startsWith("<")) {
          throw new Error("Expected JSON but received HTML. Check the data URL/path.");
        }

        const json = JSON.parse(text);
        setRows(Array.isArray(json.data) ? json.data : []);
        setAsOf(json.as_of || null);
      } catch (e) {
        console.error(e);
        setError(e.message || "Failed to load screener data.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  
  useEffect(() => {
    async function loadSpyDownStats() {
      try {
        const res = await fetch(RETURNS_URL);
        const csv = await res.text();

        const lines = csv.trim().split(/\r?\n/);
        if (lines.length < 2) return;

        const headers = parseCsvLine(lines[0]).map((h) => String(h || "").trim().toUpperCase());
        const spyIndex = headers.indexOf("SPY");
        if (spyIndex === -1) return;

        const stockTickers = new Set(
          rows
            .filter((row) => !isLikelyEtf(row))
            .map((row) => String(row.ticker || "").trim().toUpperCase())
            .filter(Boolean)
        );

        const tickerIndexes = headers
          .map((ticker, index) => ({ ticker, index }))
          .filter((x) => x.index !== spyIndex && stockTickers.has(x.ticker));

        const stats = {};
        for (const { ticker } of tickerIndexes) {
          stats[ticker] = {
            spyDown: 0,
            stockUpWhenSpyDown: 0,
            spyCrash: 0,
            stockUpWhenSpyCrash: 0,
          };
        }

        for (let r = 1; r < lines.length; r++) {
          const cols = parseCsvLine(lines[r]);
          const spy = Number(cols[spyIndex]) / 100;

          if (!Number.isFinite(spy)) continue;

          const isSpyDown = spy < 0;
          const isSpyCrash = spy <= -0.015;

          if (!isSpyDown && !isSpyCrash) continue;

          for (const { ticker, index } of tickerIndexes) {
            const stock = Number(cols[index]) / 100;
            if (!Number.isFinite(stock)) continue;

            if (isSpyDown) {
              stats[ticker].spyDown++;
              if (stock > 0) stats[ticker].stockUpWhenSpyDown++;
            }

            if (isSpyCrash) {
              stats[ticker].spyCrash++;
              if (stock > 0) stats[ticker].stockUpWhenSpyCrash++;
            }
          }
        }

        const finalStats = {};
        for (const [ticker, s] of Object.entries(stats)) {
          finalStats[ticker] = {
            pctUpWhenSpyDown:
              s.spyDown > 0 ? s.stockUpWhenSpyDown / s.spyDown : null,
            pctUpWhenSpyCrash:
              s.spyCrash > 0 ? s.stockUpWhenSpyCrash / s.spyCrash : null,
          };
        }

        setSpyStats(finalStats);
      } catch (err) {
        console.error(err);
      }
    }

    if (rows.length) loadSpyDownStats();
  }, [rows]);

const enrichedRows = useMemo(() => {
    return rows
      .filter((row) => !isLikelyEtf(row))
      .map((row) => ({
      ...row,
      category: categorizeStock(row),
      pctUpWhenSpyDown:
        spyStats?.[row.ticker]?.pctUpWhenSpyDown ?? null,
      pctUpWhenSpyCrash:
        spyStats?.[row.ticker]?.pctUpWhenSpyCrash ?? null,

    }));
  }, [rows, spyStats]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    let data = enrichedRows.filter((r) => {
      const ticker = String(r.ticker || "").toLowerCase();
      const name = String(r.name || "").toLowerCase();
      const category = String(r.category || "").toLowerCase();

      const matchesSearch =
        !q || ticker.includes(q) || name.includes(q) || category.includes(q);

      return (
        matchesSearch &&
        passesRange(r.marketCap, filters.minMarketCap, filters.maxMarketCap, 1_000_000_000) &&
        passesRange(r.peg, filters.minPeg, filters.maxPeg) &&
        passesRange(r.forwardPE, filters.minForwardPE, filters.maxForwardPE) &&
        passesRange(r.pb, filters.minPB, filters.maxPB) &&
        passesRange(r.ptbv, filters.minPTBV, filters.maxPTBV) &&
        passesRange(r.buybackYield, filters.minBuybackYield, filters.maxBuybackYield, 0.01) &&
        passesRange(r.growthRate, filters.minGrowthRate, filters.maxGrowthRate, 0.01) &&
        passesRange(
          r.institutionalOwnership,
          filters.minInstitutionalOwnership,
          filters.maxInstitutionalOwnership,
          0.01
        ) &&
        passesRange(r.rsi14, filters.minRsi14, filters.maxRsi14) &&
        passesRange(r.beta, filters.minBeta, filters.maxBeta) &&
        passesRange(
          r.pctAbove52WeekLow,
          filters.minPctAbove52WeekLow,
          filters.maxPctAbove52WeekLow,
          0.01
        )
      );
    });

    data.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;

      const an = Number(av);
      const bn = Number(bv);

      if (Number.isFinite(an) && Number.isFinite(bn)) {
        return sortDir === "asc" ? an - bn : bn - an;
      }

      const cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });

    return data;
  }, [enrichedRows, search, sortKey, sortDir, filters]);

  // const metrics = useMemo(() => {
  //   const count = filteredRows.length;
  //   const totalMarketCap = filteredRows.reduce((sum, r) => sum + safeNumber(r.marketCap), 0);
  //   const avgBeta = average(filteredRows.map((r) => r.beta));
  //   const avgFcfYield = average(filteredRows.map((r) => ratio(r.fcf, r.marketCap)));

  //   return {
  //     count,
  //     totalMarketCap,
  //     avgBeta,
  //     avgFcfYield,
  //   };
  // }, [filteredRows]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setFilters(initialFilters);
  };

  const exportCsv = () => {
    const header = visibleColumns.map((c) => c.label).join(",");
    const body = filteredRows
      .map((row) =>
        visibleColumns
          .map((c) => {
            const value = row[c.key];
            return `"${value === null || value === undefined ? "" : String(value).replaceAll('"', '""')}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([header + "\n" + body], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "majorah_stock_screener.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={pageShell}>
      <div style={heroCard}>
        <div style={headerRow}>
          <div>
            <div style={eyebrow}>Majorah Intelligence</div>
            <h1 style={title}>Stock Screener</h1>
            <p style={subtitle}>
              All metrics provided by the Majorah data pipeline.
              {asOf ? ` As of ${asOf}.` : ""}
            </p>
          </div>

          <div style={buttonRow}>
            <button onClick={clearFilters} style={secondaryButton}>
              Clear Filters
            </button>
            <button onClick={exportCsv} style={button}>
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div style={filterCard}>
        <div style={filterHeaderRow}>
          <div>
            <h2 style={sectionTitle}>Filters</h2>
            <p style={sectionSubtitle}>Search by ticker, company, or category.</p>
          </div>
          <div style={resultPill}>
            Showing <strong>{filteredRows.length}</strong> of <strong>{rows.length}</strong>
          </div>
        </div>

        <div style={controls}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ticker, company, or category..."
            style={searchInput}
          />

          <FilterInput label="Min Market Cap $B" value={filters.minMarketCap} onChange={(v) => updateFilter("minMarketCap", v)} />
          <FilterInput label="Max Market Cap $B" value={filters.maxMarketCap} onChange={(v) => updateFilter("maxMarketCap", v)} />

          <FilterInput label="Min PEG" value={filters.minPeg} onChange={(v) => updateFilter("minPeg", v)} />
          <FilterInput label="Max PEG" value={filters.maxPeg} onChange={(v) => updateFilter("maxPeg", v)} />

          <FilterInput label="Min Forward P/E" value={filters.minForwardPE} onChange={(v) => updateFilter("minForwardPE", v)} />
          <FilterInput label="Max Forward P/E" value={filters.maxForwardPE} onChange={(v) => updateFilter("maxForwardPE", v)} />

          <FilterInput label="Min P/B" value={filters.minPB} onChange={(v) => updateFilter("minPB", v)} />
          <FilterInput label="Max P/B" value={filters.maxPB} onChange={(v) => updateFilter("maxPB", v)} />

          <FilterInput label="Min P/TBV" value={filters.minPTBV} onChange={(v) => updateFilter("minPTBV", v)} />
          <FilterInput label="Max P/TBV" value={filters.maxPTBV} onChange={(v) => updateFilter("maxPTBV", v)} />

          <FilterInput label="Min Buyback Yield %" value={filters.minBuybackYield} onChange={(v) => updateFilter("minBuybackYield", v)} />
          <FilterInput label="Max Buyback Yield %" value={filters.maxBuybackYield} onChange={(v) => updateFilter("maxBuybackYield", v)} />

          <FilterInput label="Min Growth Rate %" value={filters.minGrowthRate} onChange={(v) => updateFilter("minGrowthRate", v)} />
          <FilterInput label="Max Growth Rate %" value={filters.maxGrowthRate} onChange={(v) => updateFilter("maxGrowthRate", v)} />

          <FilterInput
            label="Min Inst. Ownership %"
            value={filters.minInstitutionalOwnership}
            onChange={(v) => updateFilter("minInstitutionalOwnership", v)}
          />
          <FilterInput
            label="Max Inst. Ownership %"
            value={filters.maxInstitutionalOwnership}
            onChange={(v) => updateFilter("maxInstitutionalOwnership", v)}
          />

          <FilterInput label="Min RSI 14" value={filters.minRsi14} onChange={(v) => updateFilter("minRsi14", v)} />
          <FilterInput label="Max RSI 14" value={filters.maxRsi14} onChange={(v) => updateFilter("maxRsi14", v)} />

          <FilterInput label="Min Beta" value={filters.minBeta} onChange={(v) => updateFilter("minBeta", v)} />
          <FilterInput label="Max Beta" value={filters.maxBeta} onChange={(v) => updateFilter("maxBeta", v)} />

          <FilterInput
            label="Min % Above 52W Low"
            value={filters.minPctAbove52WeekLow}
            onChange={(v) => updateFilter("minPctAbove52WeekLow", v)}
          />
          <FilterInput
            label="Max % Above 52W Low"
            value={filters.maxPctAbove52WeekLow}
            onChange={(v) => updateFilter("maxPctAbove52WeekLow", v)}
          />
        </div>
      </div>

      {loading ? (
        <div style={message}>Loading screener data...</div>
      ) : error ? (
        <div style={errorBox}>{error}</div>
      ) : (
        <div style={tableCard}>
          <div style={tableToolbar}>
            <div>
              <h2 style={sectionTitle}>Results</h2>
              <p style={sectionSubtitle}>Click a ticker to open its details page.</p>
            </div>
          </div>

          <div style={tableWrap}>
            <table
              style={{
                ...table,
                minWidth: isMobile ? 560 : 3400,
              }}
            >
              <thead>
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={getHeaderStyle(col)}
                    >
                      {col.label}
                      {sortKey === col.key ? (
                        <span style={sortIndicator}>{sortDir === "asc" ? " ▲" : " ▼"}</span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((r, index) => (
                  <tr key={`${r.ticker}-${index}`} style={index % 2 === 0 ? rowEven : rowOdd}>
                    {visibleColumns.map((col) => (
                      <td key={col.key} style={getCellStyle(col)}>
                        {renderCell(r, col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function renderCell(row, col) {
  if (col.key === "ticker") {
    const ticker = String(row.ticker || "").trim().toUpperCase();

    if (!ticker) return "—";

    return (
      <Link to={`${STOCK_DETAIL_PATH}/${encodeURIComponent(ticker)}`} style={tickerLink}>
        {ticker}
      </Link>
    );
  }

  if (col.key === "category") {
    return <span style={getCategoryPillStyle(row.category)}>{row.category || "Uncategorized"}</span>;
  }

  return formatValue(row[col.key], col.type);
}

function FilterInput({ label, value, onChange }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={label}
      type="number"
      style={smallInput}
    />
  );
}

// function MetricCard({ label, value }) {
//   return (
//     <div style={metricCard}>
//       <div style={metricLabel}>{label}</div>
//       <div style={metricValue}>{value}</div>
//     </div>
//   );
// }



function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < String(line || "").length; i++) {
    const ch = String(line || "")[i];
    const next = String(line || "")[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }

  out.push(cur);
  return out;
}

function isLikelyEtf(row) {
  const ticker = String(row?.ticker || "").trim().toUpperCase();
  const name = String(row?.name || "").toLowerCase();

  const etfTickers = new Set([
    "SPY","QQQ","IWM","DIA","XLE","XLF","XLK","XLV","XLI","XLP",
    "XLU","XLY","ARKK","VTI","VOO","SCHD","VEA","EEM","TLT","IEF",
    "HYG","JNK","BIL","GLD","DBC","XME","COPX","PSQ","SH"
  ]);

  if (etfTickers.has(ticker)) return true;

  return (
    name.includes(" etf") ||
    name.includes("exchange traded") ||
    name.includes("fund") ||
    name.includes("trust") ||
    name.includes("ishares") ||
    name.includes("spdr") ||
    name.includes("vanguard") ||
    name.includes("invesco") ||
    name.includes("proshares") ||
    name.includes("direxion")
  );
}

function categorizeStock(row) {
  const beta = safeNumberOrNull(row.beta);
  const marketCap = safeNumberOrNull(row.marketCap);
  const growth = safeNumberOrNull(row.growthRate);
  const grossMargin = safeNumberOrNull(row.grossMargin);

  const largeCap = marketCap !== null && marketCap >= 50_000_000_000;
  const smallCap = marketCap !== null && marketCap < 10_000_000_000;

  if (beta !== null && beta < 0.9) {
    return "Defensive";
  }

  if (
    largeCap &&
    growth !== null &&
    growth > 0.1 &&
    grossMargin !== null &&
    grossMargin > 0.45
  ) {
    return "Large Cap Growth";
  }

  if (
    beta !== null &&
    beta > 1.4 &&
    growth !== null &&
    growth > 0.12
  ) {
    return "High Beta Growth";
  }

  if (beta !== null && beta > 1.1) {
    return "Cyclical";
  }

  if (smallCap) {
    return "Small Cap";
  }

  return "Balanced";
}

function passesRange(rawValue, minInput, maxInput, inputScale = 1) {
  const hasMin = minInput !== "";
  const hasMax = maxInput !== "";

  if (!hasMin && !hasMax) return true;

  const value = Number(rawValue);

  if (!Number.isFinite(value)) return false;

  const min = hasMin ? Number(minInput) * inputScale : null;
  const max = hasMax ? Number(maxInput) * inputScale : null;

  if (hasMin && value < min) return false;
  if (hasMax && value > max) return false;

  return true;
}

const columns = [
  {
    key: "ticker",
    label: "Ticker",
    sticky: true,
    left: 0,
    width: 92,
  },
  {
    key: "name",
    label: "Name",
    sticky: true,
    left: 92,
    width: 260,
  },
  {
    key: "price",
    label: "Price",
    type: "money",
    align: "right",
    sticky: true,
    left: 352,
    width: 110,
  },
  {
    key: "marketCap",
    label: "Market Cap",
    type: "moneyLarge",
    align: "right",
    sticky: true,
    left: 462,
    width: 140,
  },
  {
    key: "category",
    label: "Category",
    sticky: true,
    left: 602,
    width: 190,
  },

  { key: "rsi14", label: "RSI 14", type: "ratio", align: "right" },
  { key: "beta", label: "Beta", type: "ratio", align: "right" },
  { key: "fiftyTwoWeekLow", label: "52W Low", type: "money", align: "right" },
  { key: "pctAbove52WeekLow", label: "% Above 52W Low", type: "percent", align: "right" },

  { key: "enterpriseValue", label: "Enterprise Value", type: "moneyLarge", align: "right" },
  { key: "revenue", label: "Revenue", type: "moneyLarge", align: "right" },
  { key: "eps", label: "EPS", type: "money", align: "right" },
  { key: "ebitda", label: "EBITDA", type: "moneyLarge", align: "right" },
  { key: "cash", label: "Cash", type: "moneyLarge", align: "right" },
  { key: "debt", label: "Debt", type: "moneyLarge", align: "right" },
  { key: "sharesOutstanding", label: "Shares Out.", type: "shares", align: "right" },
  { key: "equity", label: "Equity", type: "moneyLarge", align: "right" },
  { key: "tangibleBookValue", label: "Tangible Book", type: "moneyLarge", align: "right" },
  { key: "workingCapital", label: "Working Capital", type: "moneyLarge", align: "right" },
  { key: "ocf", label: "OCF", type: "moneyLarge", align: "right" },
  { key: "fcf", label: "FCF", type: "moneyLarge", align: "right" },
  { key: "capex", label: "Capex", type: "moneyLarge", align: "right" },
  { key: "peg", label: "PEG", type: "ratio", align: "right" },
  { key: "pb", label: "P/B", type: "ratio", align: "right" },
  { key: "ptbv", label: "P/TBV", type: "ratio", align: "right" },
  { key: "forwardPE", label: "Forward P/E", type: "ratio", align: "right" },
  { key: "institutionalOwnership", label: "Inst. Ownership", type: "percent", align: "right" },
  { key: "growthRate", label: "Growth Rate", type: "percent", align: "right" },
  { key: "buybackYield", label: "Buyback Yield", type: "percent", align: "right" },
  //{ key: "dividendYield", label: "Dividend Yield", type: "percent", align: "right" },
  { key: "payoutRatio", label: "Payout Ratio", type: "percent", align: "right" },
  { key: "grossMargin", label: "Gross Margin", type: "percent", align: "right" },
  { key: "operatingMargin", label: "Operating Margin", type: "percent", align: "right" },
  { key: "profitMargin", label: "Profit Margin", type: "percent", align: "right" },
  { key: "debtEquity", label: "Debt/Equity", type: "ratio", align: "right" },
  { key: "currentRatio", label: "Current Ratio", type: "ratio", align: "right" },
  {
    key: "pctUpWhenSpyDown",
    label: "% Up When SPY Down",
    type: "percent",
    align: "right",
  },
  {
    key: "pctUpWhenSpyCrash",
    label: "% Up When SPY Down >1.5%",
    type: "percent",
    align: "right",
  },
];

function getHeaderStyle(col) {
  const base = col.align === "right" ? thRight : thLeft;

  if (!col.sticky) {
    return base;
  }

  return {
    ...base,
    position: "sticky",
    left: col.left,
    minWidth: col.width,
    width: col.width,
    maxWidth: col.width,
    zIndex: 7,
    background: "#f8fafc",
    boxShadow: "2px 0 0 rgba(148, 163, 184, 0.18)",
  };
}

function getCellStyle(col) {
  const base = col.align === "right" ? tdRight : td;

  if (!col.sticky) {
    return base;
  }

  return {
    ...base,
    position: "sticky",
    left: col.left,
    minWidth: col.width,
    width: col.width,
    maxWidth: col.width,
    zIndex: 4,
    background: "#ffffff",
    boxShadow: "2px 0 0 rgba(226, 232, 240, 0.8)",
    overflow: col.key === "name" ? "hidden" : undefined,
    textOverflow: col.key === "name" ? "ellipsis" : undefined,
  };
}

function formatValue(value, type) {
  if (value === null || value === undefined || value === "" || Number.isNaN(value)) {
    return "—";
  }

  const n = Number(value);

  if (type === "moneyLarge") {
    return formatLargeMoney(n);
  }

  if (type === "money") {
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : "—";
  }

  if (type === "shares") {
    return formatLargeNumber(n);
  }

  if (type === "percent") {
    return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : "—";
  }

  if (type === "ratio") {
    return Number.isFinite(n) ? n.toFixed(2) : "—";
  }

  return String(value);
}

// function formatMetric(value, type) {
//   if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
//   return formatValue(value, type);
// }

function formatLargeMoney(n) {
  if (!Number.isFinite(n)) return "—";

  const abs = Math.abs(n);

  if (abs >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(2)}T`;
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;

  return `$${n.toFixed(0)}`;
}

function formatLargeNumber(n) {
  if (!Number.isFinite(n)) return "—";

  const abs = Math.abs(n);

  if (abs >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(2)}T`;
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(2)}K`;

  return n.toFixed(0);
}

// function safeNumber(value) {
//   const n = Number(value);
//   return Number.isFinite(n) ? n : 0;
// }

function safeNumberOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// function ratio(numerator, denominator) {
//   const n = safeNumberOrNull(numerator);
//   const d = safeNumberOrNull(denominator);
//   if (n === null || d === null || d === 0) return null;
//   return n / d;
// }

// function average(values) {
//   const clean = values.map(Number).filter(Number.isFinite);
//   if (!clean.length) return null;
//   return clean.reduce((sum, value) => sum + value, 0) / clean.length;
// }

function getCategoryPillStyle(category) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: "nowrap",
    border: "1px solid transparent",
  };

  const styles = {
    "Defensive Cash Generator": {
      background: "#ecfdf5",
      color: "#047857",
      borderColor: "#a7f3d0",
    },
    "Value Compounder": {
      background: "#eff6ff",
      color: "#1d4ed8",
      borderColor: "#bfdbfe",
    },
    "High-Growth Quality": {
      background: "#f5f3ff",
      color: "#6d28d9",
      borderColor: "#ddd6fe",
    },
    "Cyclical Growth": {
      background: "#fff7ed",
      color: "#c2410c",
      borderColor: "#fed7aa",
    },
    "Asset-Heavy Operator": {
      background: "#f8fafc",
      color: "#334155",
      borderColor: "#cbd5e1",
    },
    "Levered / Turnaround": {
      background: "#fff1f2",
      color: "#be123c",
      borderColor: "#fecdd3",
    },
    "Defensive Balance Sheet": {
      background: "#f0fdfa",
      color: "#0f766e",
      borderColor: "#99f6e4",
    },
    "FCF Yield": {
      background: "#fefce8",
      color: "#854d0e",
      borderColor: "#fef08a",
    },
    "Sales-at-Value": {
      background: "#eef2ff",
      color: "#4338ca",
      borderColor: "#c7d2fe",
    },
  };

  return {
    ...base,
    ...(styles[category] || {
      background: "#f1f5f9",
      color: "#475569",
      borderColor: "#e2e8f0",
    }),
  };
}

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "22px 10px 34px",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 18,
  background: "linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  borderRadius: 22,
  boxShadow: "0 20px 45px rgba(15, 23, 42, 0.18)",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  flexWrap: "wrap",
};

const eyebrow = {
  marginBottom: 8,
  color: "#93c5fd",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 800,
};

const title = {
  margin: 0,
  fontSize: 28,
  letterSpacing: "-0.04em",
  lineHeight: 1.05,
};

const subtitle = {
  maxWidth: 760,
  marginTop: 10,
  color: "#cbd5e1",
  lineHeight: 1.6,
};

const buttonRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

// const metricGrid = {
//   display: "grid",
//   gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
//   gap: 12,
//   marginTop: 24,
// };

// const metricCard = {
//   padding: 16,
//   borderRadius: 16,
//   background: "rgba(255, 255, 255, 0.09)",
//   border: "1px solid rgba(255, 255, 255, 0.16)",
//   backdropFilter: "blur(12px)",
// };

// const metricLabel = {
//   fontSize: 11,
//   color: "#cbd5e1",
//   fontWeight: 700,
//   textTransform: "uppercase",
//   letterSpacing: "0.08em",
// };

// const metricValue = {
//   marginTop: 8,
//   fontSize: 20,
//   fontWeight: 850,
//   letterSpacing: "-0.03em",
// };

const filterCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 20,
  border: "1px solid #e2e8f0",
  borderRadius: 20,
  background: "rgba(255, 255, 255, 0.88)",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
};

const filterHeaderRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: 14,
};

const sectionTitle = {
  margin: 0,
  fontSize: 15,
  letterSpacing: "-0.02em",
};

const sectionSubtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: 11,
};

const resultPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "8px 12px",
  borderRadius: 999,
  background: "#f1f5f9",
  color: "#334155",
  fontSize: 11,
  border: "1px solid #e2e8f0",
};

const controls = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const searchInput = {
  minWidth: 300,
  flex: "1 1 300px",
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  fontSize: 11,
  background: "white",
  outline: "none",
};

const smallInput = {
  width: 145,
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  fontSize: 11,
  background: "white",
  outline: "none",
};

const button = {
  padding: "10px 14px",
  border: "1px solid #38bdf8",
  background: "#38bdf8",
  color: "#082f49",
  borderRadius: 12,
  cursor: "pointer",
  fontWeight: 850,
  boxShadow: "0 10px 20px rgba(56, 189, 248, 0.22)",
};

const secondaryButton = {
  padding: "10px 14px",
  border: "1px solid rgba(255, 255, 255, 0.28)",
  background: "rgba(255, 255, 255, 0.08)",
  color: "white",
  borderRadius: 12,
  cursor: "pointer",
  fontWeight: 800,
};

const tableCard = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 20,
  background: "white",
  boxShadow: "0 16px 34px rgba(15, 23, 42, 0.1)",
};

const tableToolbar = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  marginBottom: 14,
};

const tableWrap = {
  overflowX: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  maxHeight: "78vh",
};

const table = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const thLeft = {
  textAlign: "left",
  padding: "8px 8px",
  fontSize: 11,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  whiteSpace: "nowrap",
  cursor: "pointer",
  position: "sticky",
  top: 0,
  zIndex: 6,
  fontWeight: 850,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const thRight = {
  ...thLeft,
  textAlign: "right",
};

const td = {
  padding: "7px 8px",
  fontSize: 11,
  color: "#1e293b",
  borderBottom: "1px solid #eef2f7",
  whiteSpace: "nowrap",
};

const tdRight = {
  ...td,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const rowEven = {
  background: "#ffffff",
};

const rowOdd = {
  background: "#fbfdff",
};

const tickerLink = {
  color: "#2563eb",
  textDecoration: "none",
  fontWeight: 850,
};

const sortIndicator = {
  color: "#0f172a",
  fontSize: 11,
};

const message = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 20,
  color: "#475569",
  background: "white",
  borderRadius: 16,
  border: "1px solid #e2e8f0",
};

const errorBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 14,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  borderRadius: 12,
};